import { Grid } from '@mui/material';
import { Coordinate, Language, Location } from '@nx-smartmonkey/shared/domain';
import { Checkbox, RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Modal } from '../../../../../../components/Modal';
import { SlidingMenuWithBack } from '../../../../../../components/SlidingMenuWithBack';
import * as routing from '../../../../../../constants/Routing';
import { parseAddresses } from '../../../../../../helpers/Parsers';
import { useRetrieveActiveLanguage } from '../../../../../../hooks/translations/useRetrieveActiveLanguage';
import { useErrorMiddleware } from '../../../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../../../hooks/useShowSnackbarMessage';
import Button from '../../../../../../react-components/Button';
import { Text } from '../../../../../../react-components/Text';
import { useRetrieveVehicle } from '../../../../hooks/useRetrieveVehicle';
import { EditStopAddress } from './EditStopAddress';
import { ValidateStopPill } from './ValidateStopPill';

const PADDING = 20;
export const BUTTON_HEIGHT = 52;
export const ButtonContainer = styled(Grid)`
  padding: ${PADDING}px;
  margin: 0;
  width: 100%;
  background-color: ${RoutalPalette.neutral02n};

  &::after {
    content: '';
    position: absolute;
    bottom: ${BUTTON_HEIGHT + PADDING * 2}px;
    left: 0;
    right: 0;
    height: 30px;
    width: 100%;
    background: linear-gradient(0deg, ${RoutalPalette.neutral02n}, ${RoutalPalette.neutral02n}00);
    pointer-events: none;
  }
`;

/**
 * successful = Valid address
 * warning = Got address but might not be correct
 * error = Didn't got address. Can't continue until resolve.
 */
export type ValidateStopStatus = `successful` | `warning` | `error`;

export interface ValidatedAddress {
  rawAddress: string;
  location?: Location;
  status: ValidateStopStatus;
}

interface ValidateStopsBulkProps {
  rawAddresses: string[];
  isOpen: boolean;
  onClose: () => void;
  onNotEnoughCreditsError: () => void;
  onConfirm: (validatedAddresses: ValidatedAddress[]) => void;
}

export const ValidateStopsBulk = ({
  rawAddresses,
  isOpen,
  onClose,
  onNotEnoughCreditsError,
  onConfirm,
}: ValidateStopsBulkProps) => {
  const navigate = useNavigate();
  const { data: vehicleData } = useRetrieveVehicle();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const [loading, setLoading] = useState(true);
  const [confirmingContinue, setConfirmingContinue] = useState(false);
  const [editingAddress, setEditingAddress] = useState<ValidatedAddress | undefined>(undefined);
  const [validatedAddresses, setValidatedAddresses] = useState<ValidatedAddress[]>([]);
  const [validatedAddressesDict, setValidatedAddressesDict] = useState<Record<string, ValidatedAddress>>({});
  const [onlyErrors, setOnlyErrors] = useState(false);

  const { activeLanguage } = useRetrieveActiveLanguage();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const routesWithError = validatedAddresses.filter((address) => address.status !== `successful`);

  const filterAddress = (addresses: string[]) => addresses.filter((address) => address !== ``);

  const handleClose = onClose;

  const handleUpdateValidatedAddresses = (rawAddresses: string[], validatedAddressesToAdd: ValidatedAddress[]) => {
    validatedAddressesToAdd.forEach((validatedAddress) => {
      validatedAddressesDict[validatedAddress.rawAddress] = validatedAddress;
    });

    const addressesToRemove = Object.keys(validatedAddressesDict).filter((address) => !rawAddresses.includes(address));

    addressesToRemove.forEach((address) => {
      delete validatedAddressesDict[address];
    });

    const newValidatedAddresses = rawAddresses
      .map((rawAddress) => validatedAddressesDict[rawAddress])
      .filter((validatedAddress) => !!validatedAddress);

    setValidatedAddresses(newValidatedAddresses);
    setValidatedAddressesDict(validatedAddressesDict);
  };

  useEffect(() => {
    if (!isOpen) return;

    const filteredAddresses = filterAddress(rawAddresses).filter((address) => !validatedAddressesDict[address]);

    setLoading(true);

    parseAddresses({
      addresses: filteredAddresses,
      projectId: vehicleData?.project.id,
      language: activeLanguage ?? Language.getDefaultLanguage().value,
    })
      .then((value: (Location | null)[]) => {
        const newValidatedAddresses = value.map<ValidatedAddress>((stopAddress, idx) =>
          stopAddress &&
          stopAddress.lat &&
          stopAddress.lng &&
          Coordinate.areCoordinatesValid(stopAddress.lat, stopAddress.lng)
            ? {
                rawAddress: filteredAddresses[idx],
                location: stopAddress,
                status: stopAddress.partial_match ? `warning` : `successful`,
              }
            : {
                rawAddress: filteredAddresses[idx],
                location: undefined,
                status: `error`,
              }
        );

        handleUpdateValidatedAddresses(filterAddress(rawAddresses), newValidatedAddresses);
      })
      .catch((error) => {
        console.error(`Error parsing addresses`, error);

        if (!error?.response) {
          handleClose();
          return;
        }

        if (error.response.status === 401) {
          sessionExpiredLogout();
          return;
        }

        if (error.response.data?.messageId === `highway.subscription.error.expired_subscription`) {
          navigate(routing.SUBSCRIPTION_EXPIRED);
          return;
        }

        if (error.response.data?.messageId === `highway.maps.error.not_enough_credits`) {
          onNotEnoughCreditsError();
          handleClose();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${error.response.data?.messageId ?? `network_error`}`,
        });

        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <SlidingMenuWithBack
        id="validate-stops-bulk"
        isOpen={isOpen}
        onClose={handleClose}
        direction="left"
        background={RoutalPalette.white}
        headerContent={
          <Grid item container direction="row" wrap="nowrap" alignItems="center" height="70px">
            <Text variant="h6" isBold intlId="driver.stops.create_stops.add_bulk.validate.title" />
          </Grid>
        }
        childrenContainerStyles={{ padding: 0, flexWrap: `nowrap`, justifyContent: `space-between`, overflowY: `auto` }}
        footerContent={
          <ButtonContainer container>
            <Button
              disabled={loading || validatedAddresses.filter((address) => address.status !== `error`).length === 0}
              onClick={() => {
                if (validatedAddresses.some((address) => address.status === `error`)) {
                  setConfirmingContinue(true);
                  return;
                }
                onConfirm(validatedAddresses);
              }}
              intlId="driver.stops.create_stops.add_bulk.validate.add_stops"
              intlValues={{ count: validatedAddresses.filter((address) => address.status !== `error`).length }}
              style={{
                width: `100%`,
                height: `${BUTTON_HEIGHT}px`,
                maxHeight: `${BUTTON_HEIGHT}px`,
                padding: 0,
                margin: 0,
                border: 0,
              }}
            />
          </ButtonContainer>
        }
      >
        {loading ? (
          <Grid container flexDirection="column" maxHeight="100%" overflow="auto" style={{ height: `100%` }}>
            <RoutalLoader style={{ height: `auto` }} />
          </Grid>
        ) : (
          <Grid container flexDirection="column" maxHeight="100%" overflow="auto">
            <Grid item container direction="column" wrap="nowrap" alignItems="center" padding="10px 0">
              {routesWithError.length > 0 && (
                <Grid
                  onClick={() => setOnlyErrors((prevValue) => !prevValue)}
                  padding="16px 20px 0px 20px"
                  width="100%"
                >
                  <Grid
                    height="56px"
                    padding="0px 12px"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ backgroundColor: RoutalPalette.neutral02 }}
                  >
                    <Grid flex="1" whiteSpace="break-spaces">
                      <Text
                        variant="body"
                        intlId="driver.stops.create_stops.add_bulk.validate.show_only_errors"
                        style={{ cursor: `pointer` }}
                      />
                    </Grid>
                    <Checkbox checked={onlyErrors} />
                  </Grid>
                </Grid>
              )}
              {(onlyErrors ? routesWithError : validatedAddresses).map((validatedAddress, idx) => {
                return (
                  <ValidateStopPill
                    key={validatedAddress.rawAddress}
                    rawAddress={validatedAddress.rawAddress}
                    location={validatedAddress.location}
                    status={validatedAddress.status}
                    onClick={() => setEditingAddress(validatedAddress)}
                    containerStyle={{
                      borderBottom: idx === validatedAddresses.length - 1 ? `none` : undefined,
                    }}
                  />
                );
              })}
            </Grid>
          </Grid>
        )}
      </SlidingMenuWithBack>

      <Modal
        open={confirmingContinue}
        onClose={() => setConfirmingContinue(false)}
        title={{
          intlId: `driver.stops.create_stops.add_bulk.validate.confirm_continue.title`,
        }}
        subtitle={{
          intlId: `driver.stops.create_stops.add_bulk.validate.confirm_continue.body`,
        }}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              onConfirm(validatedAddresses.filter((address) => address.status !== `error`));
              setConfirmingContinue(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      {editingAddress !== undefined && (
        <EditStopAddress
          isOpen={editingAddress !== undefined}
          initialValidatedAddress={editingAddress}
          onUpdate={(newValidatedAddress) => {
            const newValidatedAddresses = validatedAddresses.map((address) => {
              if (address === editingAddress) {
                return newValidatedAddress;
              }
              return address;
            });
            setValidatedAddresses(newValidatedAddresses);
            setEditingAddress(undefined);
          }}
          onDelete={() => {
            const newValidatedAddresses = validatedAddresses.filter((address) => address !== editingAddress);
            setValidatedAddresses(newValidatedAddresses);
            setEditingAddress(undefined);
          }}
          onClose={() => setEditingAddress(undefined)}
        />
      )}
    </>
  );
};
